import { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, Truck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { paraTexto } from '../../lib/vendas/numero';
import { confirmarColeta, lerQuantidadeColeta, montarLinhasColeta, motivoBloqueioColeta, rotuloLote, type EntradaColetaLote, type ExpedicaoDetalheApi } from '../../lib/expedicao';

export interface ColetaDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly detalhe: ExpedicaoDetalheApi;
  readonly onConfirmada: () => void;
}

function novaChave(): string { return crypto.randomUUID(); }

export function ColetaDialog({ aberto, onAbertoChange, detalhe, onConfirmada }: ColetaDialogProps) {
  const [volumes, setVolumes] = useState('1');
  const [transporte, setTransporte] = useState('RETIRADA_CLIENTE');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultadoIncerto, setResultadoIncerto] = useState(false);
  const [recarregarObrigatorio, setRecarregarObrigatorio] = useState(false);
  const [entradas, setEntradas] = useState<Record<string, EntradaColetaLote>>({});
  const chave = useRef(novaChave());
  const bloqueio = motivoBloqueioColeta(detalhe);
  const linhasAColetar = detalhe.linhas.filter((linha) => Number(linha.restante) > 0);
  const quantidadeInvalida = linhasAColetar.some((linha) => { const entrada = entradas[linha.loteId]; return !entrada || !lerQuantidadeColeta(entrada.quantidade, linha.restante).ok; });

  useEffect(() => {
    if (aberto) {
      setErro(null); setResultadoIncerto(false); setRecarregarObrigatorio(false); chave.current = novaChave();
      // L-49: a quantidade começa no restante e o usuário pode reduzir (coleta parcial); a cobertura acompanha a quantidade.
      setEntradas(Object.fromEntries(linhasAColetar.map((linha) => [linha.loteId, { quantidade: paraTexto(Number(linha.restante)), referencia: '', tipo: '', cobertura: null }])));
    }
  }, [aberto, detalhe.expedicao.retornoId]);

  async function confirmar(): Promise<void> {
    if (enviando || bloqueio || resultadoIncerto) return;
    const politica = linhasAColetar[0]?.politicaDocumental;
    if (!politica) return;
    const numeroVolumes = Number(volumes);
    if (!Number.isInteger(numeroVolumes) || numeroVolumes <= 0 || !transporte.trim()) { setErro('Informe volumes positivos e a modalidade de transporte ou retirada.'); return; }
    const montagem = montarLinhasColeta(linhasAColetar, entradas);
    if ('erro' in montagem) { setErro(montagem.erro); return; }
    setEnviando(true); setErro(null);
    try {
      const resposta = await confirmarColeta(detalhe.expedicao.retornoId, {
        chaveIdempotencia: chave.current,
        politicaDocumental: politica,
        transportadoraOuRetirada: transporte.trim(),
        volumes: numeroVolumes,
        linhas: montagem.linhas,
      });
      const corpo = await resposta.json().catch(() => null) as { readonly message?: string; readonly codigo?: string } | null;
      if (!resposta.ok) {
        if (resposta.status === 409) { setRecarregarObrigatorio(true); setErro(`${corpo?.message ?? 'A situação mudou em outra estação.'} Recarregar situação atual antes de tentar novamente.`); }
        else setErro(corpo?.message ?? 'Não foi possível confirmar a saída física.');
        return;
      }
      onConfirmada();
    } catch {
      // Uma falha de rede não informa se o POST chegou ao servidor: não reutilizamos nem geramos nova chave sem conciliar.
      setResultadoIncerto(true);
      setErro('O resultado da confirmação é incerto. Recarregue a situação atual antes de tentar novamente.');
    } finally { setEnviando(false); }
  }

  function alterar(loteId: string, parcial: Partial<EntradaColetaLote>): void {
    setEntradas((atual) => ({ ...atual, [loteId]: { ...(atual[loteId] ?? { quantidade: '', referencia: '', tipo: '', cobertura: null }), ...parcial } }));
  }

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent size="wide">
        <FormDialogHeader><FormDialogTitle>Confirmar saída física</FormDialogTitle></FormDialogHeader>
        <FormDialogBody className="space-y-4">
          <p>Revise os lotes, a documentação conferida e a modalidade antes de confirmar. Ator e data são registrados pelo servidor.</p>
          {bloqueio && <FormMessage>{bloqueio}</FormMessage>}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1"><Label htmlFor="coleta-volumes">Volumes</Label><Input id="coleta-volumes" inputMode="numeric" value={volumes} onChange={(evento) => setVolumes(evento.target.value)} disabled={enviando} /></div>
            <div className="grid gap-1"><Label htmlFor="coleta-transporte">Transporte ou retirada</Label><Input id="coleta-transporte" value={transporte} onChange={(evento) => setTransporte(evento.target.value)} disabled={enviando} /></div>
          </div>
          <div className="space-y-3">
            {linhasAColetar.map((linha) => {
              const entrada = entradas[linha.loteId] ?? { quantidade: '', referencia: '', tipo: '', cobertura: null };
              const leitura = lerQuantidadeColeta(entrada.quantidade, linha.restante);
              return (
                <fieldset key={linha.loteId} className="grid gap-2 rounded border p-3" title={linha.loteId}>
                  <legend className="px-1">Lote {rotuloLote(linha)} — restante {paraTexto(Number(linha.restante))}</legend>
                  <div className="grid gap-2 sm:grid-cols-4">
                    <div className="grid gap-1">
                      <Label htmlFor={`quantidade-${linha.loteId}`}>Quantidade a coletar</Label>
                      <Input id={`quantidade-${linha.loteId}`} inputMode="decimal" value={entrada.quantidade} aria-invalid={!leitura.ok} aria-describedby={leitura.ok ? undefined : `quantidade-erro-${linha.loteId}`} onChange={(evento) => alterar(linha.loteId, { quantidade: evento.target.value })} disabled={enviando} />
                      {!leitura.ok && <p id={`quantidade-erro-${linha.loteId}`} role="alert" className="text-sm text-destructive">{leitura.mensagem}</p>}
                    </div>
                    <div className="grid gap-1"><Label htmlFor={`documento-${linha.loteId}`}>Referência conferida</Label><Input id={`documento-${linha.loteId}`} value={entrada.referencia} onChange={(evento) => alterar(linha.loteId, { referencia: evento.target.value })} /></div>
                    <div className="grid gap-1"><Label htmlFor={`tipo-${linha.loteId}`}>Tipo</Label><Input id={`tipo-${linha.loteId}`} value={entrada.tipo} onChange={(evento) => alterar(linha.loteId, { tipo: evento.target.value })} /></div>
                    <div className="grid gap-1"><Label htmlFor={`cobertura-${linha.loteId}`}>Cobertura</Label><Input id={`cobertura-${linha.loteId}`} inputMode="decimal" value={entrada.cobertura ?? entrada.quantidade} onChange={(evento) => alterar(linha.loteId, { cobertura: evento.target.value })} /></div>
                  </div>
                </fieldset>
              );
            })}
          </div>
          {erro && <FormMessage>{erro}</FormMessage>}
        </FormDialogBody>
        <FormDialogFooter>
          {(resultadoIncerto || recarregarObrigatorio) && <Button type="button" variant="outline" onClick={onConfirmada}><RefreshCw aria-hidden="true" /> Recarregar situação atual</Button>}
          <Button type="button" disabled={Boolean(bloqueio) || quantidadeInvalida || enviando || resultadoIncerto || recarregarObrigatorio} onClick={() => void confirmar()}>{enviando ? <Loader2 aria-hidden="true" className="animate-spin" /> : <Truck aria-hidden="true" />} {enviando ? 'Confirmando…' : 'Confirmar saída física'}</Button>
        </FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
